import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMyPermissions, getDefaultPermissions, getCompanyPermissions, getCompanies } from '../lib/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

// Default empty permissions
const EMPTY_PERMISSIONS = {
  dashboard: { view: false, create: false, edit: false, delete: false, export: false },
  locations: { view: false, create: false, edit: false, delete: false, export: false },
  machines: { view: false, create: false, edit: false, delete: false, export: false },
  games: { view: false, create: false, edit: false, delete: false, export: false },
  issues: { view: false, create: false, edit: false, delete: false, export: false },
  expenses: { view: false, create: false, edit: false, delete: false, export: false },
  money_in_out: { view: false, create: false, edit: false, delete: false, export: false },
  vendors: { view: false, create: false, edit: false, delete: false, export: false },
  referers: { view: false, create: false, edit: false, delete: false, export: false },
  location_managers: { view: false, create: false, edit: false, delete: false, export: false },
  inventory: { view: false, create: false, edit: false, delete: false, export: false },
  invoices: { view: false, create: false, edit: false, delete: false, export: false },
  routes: { view: false, create: false, edit: false, delete: false, export: false },
  users: { view: false, create: false, edit: false, delete: false, export: false },
  partners: { view: false, create: false, edit: false, delete: false, export: false },
  companies: { view: false, create: false, edit: false, delete: false, export: false },
  machine_accounting: { view: false, create: false, edit: false, delete: false, export: false },
  partner_payouts: { view: false, create: false, edit: false, delete: false, export: false },
  role_permissions: { view: false, create: false, edit: false, delete: false, export: false },
  activity_logs: { view: false, create: false, edit: false, delete: false, export: false },
  analytics: { view: false, create: false, edit: false, delete: false, export: false },
  subscription: { view: false, create: false, edit: false, delete: false, export: false },
};

// Roles that can use preview mode
const PREVIEW_ALLOWED_ROLES = ['super_admin', 'admin', 'demo_admin'];

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [effectiveRole, setEffectiveRole] = useState(null);

  // --- Role Preview State (persisted in sessionStorage) ---
  const [previewRole, setPreviewRole] = useState(() => {
    try { return sessionStorage.getItem('slotfleet_preview_role') || null; } catch { return null; }
  });
  const [previewPermissions, setPreviewPermissions] = useState(() => {
    try { const p = sessionStorage.getItem('slotfleet_preview_perms'); return p ? JSON.parse(p) : null; } catch { return null; }
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [realPermissions, setRealPermissions] = useState(null);

  const isPreviewActive = !!previewRole && !!previewPermissions;
  const canUsePreview = PREVIEW_ALLOWED_ROLES.includes(user?.role);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions(EMPTY_PERMISSIONS);
      setLoading(false);
      return;
    }

    try {
      const response = await getMyPermissions();
      const data = response.data;
      
      setIsSuperAdmin(data.is_super_admin || false);
      setPermissions(data.permissions || EMPTY_PERMISSIONS);
      setRealPermissions(data.permissions || EMPTY_PERMISSIONS);
      setEffectiveRole(data.role || user?.role || null);
    } catch (error) {
      setPermissions(EMPTY_PERMISSIONS);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Load available roles for preview dropdown
  useEffect(() => {
    if (!canUsePreview || !isAuthenticated) return;
    let cancelled = false;
    getDefaultPermissions()
      .then(res => {
        if (cancelled) return;
        const defaults = res.data?.defaults || {};
        setAvailableRoles(Object.keys(defaults));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canUsePreview, isAuthenticated]);

  // Start previewing a role — checks all companies for most permissive permissions
  const startPreview = useCallback(async (role) => {
    if (!canUsePreview || !role) return;
    try {
      let rolePerms = null;

      // Get all companies to check for custom permissions
      const companiesRes = await getCompanies(1, 100);
      const allCompanies = companiesRes.data?.data || companiesRes.data || [];

      // Check each company for custom permissions, merge most permissive
      for (const company of allCompanies) {
        try {
          const companyRes = await getCompanyPermissions(company.id);
          const companyPerms = companyRes.data?.[role];
          if (companyPerms && !companyPerms.is_default && companyPerms.permissions) {
            if (!rolePerms) {
              rolePerms = JSON.parse(JSON.stringify(companyPerms.permissions));
            } else {
              // Merge: most permissive wins
              for (const [page, actions] of Object.entries(companyPerms.permissions)) {
                if (!rolePerms[page]) rolePerms[page] = actions;
                else if (typeof actions === 'object') {
                  for (const [action, allowed] of Object.entries(actions)) {
                    if (allowed) rolePerms[page][action] = true;
                  }
                }
              }
            }
          }
        } catch {}
      }

      // Fall back to system defaults
      if (!rolePerms) {
        const res = await getDefaultPermissions();
        const defaults = res.data?.defaults || {};
        rolePerms = defaults[role];
      }

      if (rolePerms) {
        setPreviewRole(role);
        setPreviewPermissions(rolePerms);
        try {
          sessionStorage.setItem('slotfleet_preview_role', role);
          sessionStorage.setItem('slotfleet_preview_perms', JSON.stringify(rolePerms));
        } catch {}
      }
    } catch (error) {
    }
  }, [canUsePreview]);

  // Stop previewing
  const stopPreview = useCallback(() => {
    setPreviewRole(null);
    setPreviewPermissions(null);
    try {
      sessionStorage.removeItem('slotfleet_preview_role');
      sessionStorage.removeItem('slotfleet_preview_perms');
    } catch {}
  }, []);

  // Active permissions = preview or real
  const activePermissions = isPreviewActive ? previewPermissions : permissions;
  const activeIsSuperAdmin = isPreviewActive ? (previewRole === 'super_admin') : isSuperAdmin;
  const activeRole = isPreviewActive ? previewRole : effectiveRole;

  // Check if user can perform action on a page
  const can = useCallback((page, action = 'view') => {
    // During preview, use preview role logic
    if (isPreviewActive) {
      if (previewRole === 'demo_admin') {
        return action === 'view' || action === 'export';
      }
      if (previewRole === 'super_admin') return true;
      const pagePerms = previewPermissions?.[page];
      if (!pagePerms) return false;
      return pagePerms[action] || false;
    }

    // Normal mode
    if (user?.role === 'demo_admin') {
      return action === 'view' || action === 'export';
    }
    if (isSuperAdmin) return true;
    
    const pagePerms = permissions[page];
    if (!pagePerms) return false;
    
    return pagePerms[action] || false;
  }, [permissions, isSuperAdmin, user, isPreviewActive, previewRole, previewPermissions]);

  const canView = useCallback((page) => can(page, 'view'), [can]);
  const canCreate = useCallback((page) => can(page, 'create'), [can]);
  const canEdit = useCallback((page) => can(page, 'edit'), [can]);
  const canDelete = useCallback((page) => can(page, 'delete'), [can]);
  const canExport = useCallback((page) => can(page, 'export'), [can]);
  const canAny = useCallback((page, actions) => actions.some(action => can(page, action)), [can]);
  const canAll = useCallback((page, actions) => actions.every(action => can(page, action)), [can]);

  const value = {
    permissions: activePermissions,
    loading,
    isSuperAdmin: activeIsSuperAdmin,
    effectiveRole: activeRole,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canExport,
    canAny,
    canAll,
    refreshPermissions: loadPermissions,
    // Preview API
    previewRole,
    isPreviewActive,
    canUsePreview,
    availableRoles,
    startPreview,
    stopPreview,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export default PermissionsContext;
